/**
 * Main Navigation Sidebar Component 
 * 
 * Replicates the left sidebar navigation from master.html template
 * with vertical tabs linking to existing AngularJS modules.
 * 
 * Issue #74 - Master Layout Migration
 */

import { useLocation, Link } from 'react-router-dom';
import { getFeatureFlags, getNavigationUrls, shouldUseReactVersion, type TenantConfig } from '../../config/featureFlags';
import './MainNavbar.css';

interface Tab {
  name: string;
  sprite: string;
  url: string;
  permissions?: string | string[];
  active: boolean;
  isReactVersion?: boolean;
  fallbackUrl?: string;
}

interface MainNavbarProps {
  globalContext?: {
    authContext?: {
      employee?: {
        permissions?: string[];
        dashboard?: string;
        has_app_permission?: (perm: string) => boolean;
      };
      permissions?: string[];
      user?: {
        id?: number;
        username?: string;
        email?: string;
        is_staff?: boolean;
        is_superuser?: boolean;
      };
    };
    appContext?: {
      singleUser?: boolean;
      version?: string;
      environment?: string;
      patientsLandingPage?: string;
    };
  };
}

function MainNavbar({ globalContext }: MainNavbarProps) {
  const location = useLocation();

  // Build tenant configuration for feature flag resolution
  const tenantConfig: TenantConfig = {
    singleUser: globalContext?.appContext?.singleUser,
    environment: (globalContext?.appContext?.environment as 'development' | 'staging' | 'production') || 'development',
    // Add tenant-specific overrides if needed
    featureOverrides: {},
  };

  // Get feature flags for current tenant/environment
  const featureFlags = getFeatureFlags(tenantConfig);
  const navigationUrls = getNavigationUrls(featureFlags, tenantConfig);

  // Permission checking function
  const hasPermission = (perms?: string | string[]): boolean => {
    if (!perms) return true;

    const employee = globalContext?.authContext?.employee;
    // Try multiple permission sources to match Django implementation
    const userPermissions = employee?.permissions || globalContext?.authContext?.permissions || [];

    // Check for superuser first (matches Django logic)
    if (globalContext?.authContext?.user?.is_superuser) {
      return true;
    }

    if (typeof perms === 'string') {
      return userPermissions.includes(perms);
    }

    // If array, check if user has any of the permissions
    return perms.some(perm => userPermissions.includes(perm));
  };

  // Determine active tab based on current location
  const getActiveTab = (): string => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/directory')) return 'directory';
    if (path.startsWith('/patients')) return 'patients';
    if (path.startsWith('/encounters')) return 'encounter';
    // TECdoc Scribe: /ai/* is the single_user tenant's EMR tab
    if (path.startsWith('/ai')) return 'encounter';
    if (path.startsWith('/hr')) return 'employees';
    if (path.startsWith('/reports')) return 'reports';
    if (path.startsWith('/admin')) return 'admin';
    return '';
  };

  const activeTab = getActiveTab();

  // Build tabs array matching view_master.py logic
  const getTabs = (): Tab[] => {
    const isSingleUser = globalContext?.appContext?.singleUser;
    const employee = globalContext?.authContext?.employee;
    const dashboard = employee?.dashboard;
    const dashboardUrl =
      dashboard === 'quality' && !hasPermission('quality_dashboard')
        ? '/dashboard'
        : dashboard
          ? `/dashboard/#/${dashboard}`
          : '/dashboard';

    if (isSingleUser) {
      return [
        {
          name: 'EMR',
          sprite: 'clipboard',
          url: '/ai/list',
          permissions: 'patient_encounter',
          active: activeTab === 'encounter',
          // TECdoc Scribe patient list is React now — route client-side
          isReactVersion: true
        },
        {
          name: 'People',
          sprite: 'stethoscope',
          url: navigationUrls.hr,
          permissions: 'hr_admin',
          active: activeTab === 'employees',
          isReactVersion: shouldUseReactVersion('hr', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/hr/' // AngularJS fallback
        },
        {
          name: 'Admin',
          sprite: 'gear',
          url: navigationUrls.admin,
          permissions: 'admin',
          active: activeTab === 'admin',
          isReactVersion: shouldUseReactVersion('admin', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/admin/main'
        }
      ];
    } else {
      return [
        {
          name: 'Dashboard',
          sprite: 'graph',
          url: dashboardUrl,
          permissions: ['dashboard', 'quality_dashboard'],
          active: activeTab === 'dashboard'
        },
        {
          name: 'Directory',
          sprite: 'people',
          url: navigationUrls.directory,
          permissions: 'directory',
          active: activeTab === 'directory',
          isReactVersion: shouldUseReactVersion('directory', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/directory/info' // AngularJS fallback
        },
        {
          name: 'Patients',
          sprite: 'bandaid',
          url: navigationUrls.patients,
          permissions: ['patient_enroll', 'patient_encounter', 'patient_history'],
          active: activeTab === 'patients',
          isReactVersion: shouldUseReactVersion('patients', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/patients/' // AngularJS fallback
        },
        {
          name: 'EMR',
          sprite: 'clipboard',
          url: '/encounters',
          permissions: 'patient_encounter',
          active: activeTab === 'encounter',
          isReactVersion: true
        },
        {
          name: 'People',
          sprite: 'stethoscope',
          url: navigationUrls.hr,
          permissions: 'hr_admin',
          active: activeTab === 'employees',
          isReactVersion: shouldUseReactVersion('hr', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/hr/' // AngularJS fallback
        },
        {
          name: 'Reports',
          sprite: 'pencil',
          url: navigationUrls.reports,
          permissions: 'generate_reports',
          active: activeTab === 'reports',
          isReactVersion: shouldUseReactVersion('reports', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/reports/' // AngularJS fallback
        },
        {
          name: 'Admin',
          sprite: 'gear',
          url: navigationUrls.admin,
          permissions: 'admin',
          active: activeTab === 'admin',
          isReactVersion: shouldUseReactVersion('admin', featureFlags, employee?.permissions, tenantConfig),
          fallbackUrl: '/admin/main'
        }
      ];
    }
  };

  const allTabs = getTabs();
  const tabs = allTabs.filter(tab => hasPermission(tab.permissions));
  

  // Handle link clicks with fallback support
  const handleNavClick = (tab: Tab, _event: React.MouseEvent<HTMLAnchorElement>) => {
    // For React routes, let React Router handle navigation
    if (tab.isReactVersion && tab.url.startsWith('/') && !tab.url.startsWith('//')) {
      // React Router will handle this - no special action needed
      return;
    }

    // For external links or AngularJS routes, use normal navigation
    // This maintains backward compatibility
  };

  return (
    <nav className="main-navbar">
      <ul className="main-navbar-tabs">
        {tabs.map((tab, index) => (
          <li key={index} className={tab.active ? 'activeTab' : ''}>
            {tab.isReactVersion ? (
              // Use React Router Link for React routes - no page reload
              <Link
                to={tab.url}
                className={`${tab.active ? 'tec-sprite-white' : 'tec-sprite-red'} tec-sprite-${tab.sprite}-md react-route`}
                title={tab.name}
                data-fallback-url={tab.fallbackUrl}
              >
                {/* Sprite icon is handled via CSS classes */}
              </Link>
            ) : (
              // Use regular anchor for AngularJS routes - full page navigation
              <a
                href={tab.url}
                className={`${tab.active ? 'tec-sprite-white' : 'tec-sprite-red'} tec-sprite-${tab.sprite}-md angular-route`}
                title={`${tab.name} (AngularJS)`}
                onClick={(e) => handleNavClick(tab, e)}
                data-fallback-url={tab.fallbackUrl}
              >
                {/* Sprite icon is handled via CSS classes */}
              </a>
            )}
          </li>
        ))}
      </ul>
      
      {/* Version and environment info at bottom */}
      <div className="main-navbar-info">
        <span className="deployed-environment">
          {globalContext?.appContext?.environment}
        </span>
        <span className="version-number">
          {globalContext?.appContext?.version}
        </span>
      </div>
    </nav>
  );
}

export default MainNavbar;
